// @flow
import React, { useEffect, useCallback } from "react";
import { useHistory } from "react-router-dom";
import { connect } from "react-redux";
import { createStructuredSelector } from "reselect";
import { round } from "lodash";
import glamorous from "glamorous";
import { css } from "glamor";
import cx from "classnames";
import t from "common/i18n";
import { bindStructuredActions } from "common/actionsBase";
import bindRootActions, { types as rootAts } from "root/actions";
import { NOTIFICATION_IDS } from "root/data/NotificationsData";
import TabFooter from "root/components/TabFooter";
import CloseButton from "root/components/CloseButton/CloseButton";
import Button from "root/components/Button";
import MenuButton from "root/components/MenuButton/MenuButton";
import useExpandedView from "root/hooks/useExpandedView";
import { selectShouldShowAdvancedOptions, selectIsInlineMode } from "root/selectors";
import getMediaQuery from "utils/sassVarsUtils";
import { CLOSE_PROMPT_WIDTH } from "common/consts";
import ExpandedItem from "queue/components/ExpandedItem/ExpandedItem";
import {
	selectCropSources,
	selectCrop,
	selectDefaultCrop,
	selectCropOptions,
	selectShowSkipCropButton,
} from "./selectors";
import CropWidget from "./components/CropWidget/CropWidget";
import SVG_ICONS from "~/assets/icons/icons";

type Props = {
    cropSources: Object,
    crop: Object,
    cropOptions: Object,
    rootActions: { [string]: Function },
    defaultCrop: Object,
    shouldShowAdvanced: boolean,
    isInline: boolean,
    showSkipCropButton: boolean
};

const GCropView = glamorous.div(
	cx(
		"GCropView",
		"absolute left-0 right-0 bottom-0 top-0",
		"w-100 h-100",
		"block flex-column z-5",
	), {
		borderRadius: 6,
		boxShadow: "0 20px 30px rgba(0,0,0,.5)",
		[getMediaQuery("ns")]: {
			display: "flex",
		},
	},
);

const GHead = glamorous.div(cx(
	"crop-header flex bg-window ph6 bc-window-border bb",
), {
	height: "62",
});

const GTitle = glamorous.div(cx(
	"self-center relative pl5 f5 fw6 c-window-border bl",
), {
	lineHeight: "40px",
});

const GPopUpBody = glamorous.div(cx(
	"flex items-center justify-center",
	"bg-source-bg bg-source-bg-c",
), {
	height: "calc(100% - 165px)",
	[getMediaQuery("ns")]: {
		height: "calc(100% - 132px)",
	},
});

const advancedOptionStyles = css({
	"& .advance-box": {
		bottom: "70px",
		left: 0,
		[getMediaQuery("ns")]: {
			left: "72px",
			bottom: "16",
		},
	},
});

const MIN_CROP_SIZE = 100;

const isImageSmallerThanMaxConfig = ({cropOptions, imageWidth, imageHeight}) => {
	const factor = 0.001;
	const maxImageWidth = cropOptions.maxImageWidth || imageWidth + factor;
	const maxImageHeight = cropOptions.maxImageHeight || imageWidth + factor;

	return imageWidth < maxImageWidth || imageHeight < maxImageHeight;
};

const fixNumber = (number) => +round(number, 3).toFixed(3);

const configMinSizeSameAsImageSize = ({ cropOptions, imageWidth, imageHeight }) => {
	return (cropOptions.minImageWidth && imageWidth === cropOptions.minImageWidth) &&
		(cropOptions.minImageHeight && imageHeight === cropOptions.minImageHeight);
};

const configMaxSizeSameAsImageSize = ({ cropOptions, imageWidth, imageHeight }) => {
	return (cropOptions.maxImageWidth && imageWidth === cropOptions.maxImageWidth) &&
		(cropOptions.maxImageHeight && imageHeight === cropOptions.maxImageHeight);
};

const isConfigAspectRatioSameAsImage = ({ crop, cropOptions, imageWidth, imageHeight }) => {

	const croppingAspectRatio = fixNumber(cropOptions.croppingAspectRatio);

	return crop.pixelCrop && [
		fixNumber(imageWidth / imageHeight),
		fixNumber(crop.pixelCrop.width / crop.pixelCrop.height),
	].every(i => i === croppingAspectRatio);
};

const isShowSkipButton = (showSkipCropButton: boolean, crop: Object, cropOptions: Object) => {

	if (showSkipCropButton) {
		return true;
	}

	const [imageWidth, imageHeight] = crop.dimensions || [];

	const isMinSizeSameAsImageSize = configMinSizeSameAsImageSize({cropOptions, imageWidth, imageHeight});
	const isMaxSizeSameAsImageSize = configMaxSizeSameAsImageSize({cropOptions, imageWidth, imageHeight});

	return (
			isConfigAspectRatioSameAsImage({ crop, cropOptions, imageWidth, imageHeight }) ||
			isMinSizeSameAsImageSize ||
			isMaxSizeSameAsImageSize
		)
		&& (
			isImageSmallerThanMaxConfig({ crop, cropOptions, imageWidth, imageHeight }) ||
			(isMinSizeSameAsImageSize && isMaxSizeSameAsImageSize)
		);
};

export const CropView = (props: Props) => {
	const history = useHistory();

	const onError = useCallback(() => {
		props.rootActions[rootAts.SET_CROP]({ imageError: true });
	}, [props.rootActions]);

	const onCancelButtonClick = useCallback(() => {
		props.rootActions[rootAts.SET_CROP]({}); // reset crop data so if there was error loading image it wont persist
		history.goBack();
	}, [history, props.rootActions]);

	const onCloseButtonClick = useCallback(() => {
		props.rootActions[rootAts.SET_PROMPT]({
			text: t("crop.close_prompt"),
			width: CLOSE_PROMPT_WIDTH,
			actions: [
				{
					label: t("crop.close_btn"),
					color: "error",
					onClick: () => {
						props.rootActions[rootAts.CLEAR_NOTIFICATION]({ id: NOTIFICATION_IDS.PROMPT });
						onCancelButtonClick();
						props.rootActions[rootAts.DO_CLOSE_WIDGET]();
					},
				},
			],
		});
	}, [onCancelButtonClick, props.rootActions]);

	const onUploadButtonClick = (cropInfo) => {
		props.rootActions[rootAts.ADD_UPLOAD_TO_QUEUE]({
			files: props.cropSources.data.files,
			cropInfo: [{ ...cropInfo, file: props.cropSources.data.files[0] }],
		});
	};

	const onResetButtonClick = useCallback(() => {
		props.rootActions[rootAts.SET_CROP](props.defaultCrop);
	}, [props.rootActions, props.defaultCrop]);

	const backBtn = <MenuButton
		onClick={onCancelButtonClick}
		className="relative self-center mr5"
		icon={SVG_ICONS.BACK}
		ariaLabel={t("aria_label.back")}
		tabIndex={0}
		iconSize={30}/>;

	const cropIcn = <MenuButton
		className="btn-back relative self-center ml1 mr6"
		icon={SVG_ICONS.CROP}
		iconSize={24}
	/>;

	const closeBtn = !props.isInline
		? <CloseButton
			onClick={onCloseButtonClick}
			className="self-center ml5 btn-close-popup ml-auto"
			data-test="close-dialog-button"/> : null;

	useEffect(() => {
		props.rootActions[rootAts.TOGGLE_RESPONSIVE_MENU]({ show: false });
	}, [props.rootActions]);

	useExpandedView();

	const sameAsDefaultCrop = props.crop
        && props.defaultCrop
        && props.crop.width === props.defaultCrop.width
        && props.crop.height === props.defaultCrop.height;

	const cropEnabled = props.crop.width !== MIN_CROP_SIZE
		|| props.crop.height !== MIN_CROP_SIZE;

	const showSkipButton = isShowSkipButton(props.showSkipCropButton, props.crop, props.cropOptions);

	const resetBtn = <Button
		onClick={onResetButtonClick}
		className="ml-auto self-center fw9 mr3"
		text={t("crop.reset_btn")}
		icon={SVG_ICONS.RESET}
		fontSize={12}
		iconSize={20}
		disabled={sameAsDefaultCrop}
		textColorName="link"
		tabIndex={0}
	/>;

	const uploadBtn = <>
		<Button
			dataTest="cropBtn"
			onClick={() => onUploadButtonClick(props.crop)}
			className="ml3"
			text={t("crop.crop_btn")}
			disabled={!cropEnabled}
			icon={SVG_ICONS.CROP}
			colorName="action"
			fontSize={14}
			iconSize={22}
			tabIndex={0}
		/>
		{showSkipButton ? <Button
			onClick={() => onUploadButtonClick({
				...props.crop,
				width: MIN_CROP_SIZE,
				height: MIN_CROP_SIZE,
				x: 0,
				y: 0,
				pixelCrop: {
					width: 0,
					height: 0,
					x: 0,
					y: 0,
				},
			})}
			dataTest="skip-button"
			className="ml3"
			text={t("crop.skip_btn")}
			icon={SVG_ICONS.UPLOAD}
			colorName="link"
			fontSize={14}
			iconSize={22}
			tabIndex={0}
		/> : null}
	</>;

	return (
		<GCropView {...advancedOptionStyles}>
			<GHead>
				{props.cropOptions.croppingShowBackButton ? backBtn : cropIcn }
				<GTitle>
					{t("crop.title")}
				</GTitle>
				{closeBtn}
			</GHead>
			<GPopUpBody>

				{!props.crop || props.crop.imageError ? (
					<ExpandedItem
						item={{
							failed: true,
							statusText: `${t("crop.image_error")}: ${props.cropSources.src}`,
						}}
						className={cx("self-start w-100")}
						paddingBottom="60.6%"
					/>
				) : (
					<CropWidget
						cropSource={props.cropSources}
						setCrop={props.rootActions[rootAts.SET_CROP]}
						crop={props.crop}
						options={props.cropOptions}
						setDefaultCrop={props.rootActions[rootAts.SET_DEFAULT_CROP]}
						onError={onError}
					/>
				)}

			</GPopUpBody>
			<TabFooter
				actionButton={props.crop.imageError ? null : uploadBtn}
				secondaryButton={props.crop.imageError ? null : resetBtn} />
		</GCropView>
	);
};

export default connect<Props, *, _, _, _, _>(
	createStructuredSelector({
		cropSources: selectCropSources,
		crop: selectCrop,
		defaultCrop: selectDefaultCrop,
		cropOptions: selectCropOptions,
		shouldShowAdvanced: selectShouldShowAdvancedOptions,
		isInline: selectIsInlineMode,
		showSkipCropButton: selectShowSkipCropButton,
	}),
	bindStructuredActions({
		rootActions: bindRootActions,
	}),
)(CropView);
